import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ENV } from './env';
/**
 * Subscribes to the backend SSE stream and updates the React Query cache
 * for product lists + detail in response to admin actions.
 *
 * Industry pattern: one EventSource per browser tab; cache updates push
 * new data into TanStack so consumers re-render without re-fetching.
 *
 * URL: ENV.apiBaseUrl + ENV.apiPrefix + /catalog/online/events
 * Mirrors the same base as api() in api.ts.
 */
export function useCatalogLiveUpdates() {
    const qc = useQueryClient();
    useEffect(() => {
        const url = `${ENV.apiBaseUrl}${ENV.apiPrefix}/catalog/online/events`;
        // withCredentials so session cookies flow on cross-origin SSE.
        const es = new EventSource(url, { withCredentials: true });
        es.onopen = () => {
            console.info('[catalog-events] SSE connected:', url);
        };
        es.onmessage = (ev) => {
            let payload;
            try {
                payload = JSON.parse(ev.data);
            }
            catch {
                return;
            }
            if (payload.type === 'heartbeat')
                return;
            console.info('[catalog-events] received', payload.type, payload);
            switch (payload.type) {
                case 'entry.published':
                case 'entry.updated': {
                    const entry = payload.entry;
                    // Populate detail cache so ProductDetailPage hits immediately.
                    qc.setQueryData(['product', entry.publicSlug], entry);
                    // Invalidate list queries - lighter than mutating paginated pages.
                    void qc.invalidateQueries({ queryKey: ['online-catalog'] });
                    void qc.invalidateQueries({ queryKey: ['signatureProducts'] });
                    markRecentlyUpdated(entry.id, 'entry');
                    break;
                }
                case 'entry.unpublished':
                case 'entry.archived': {
                    void qc.invalidateQueries({ queryKey: ['online-catalog'] });
                    void qc.invalidateQueries({ queryKey: ['signatureProducts'] });
                    break;
                }
                case 'stock.changed': {
                    void qc.invalidateQueries({ queryKey: ['online-catalog'] });
                    markRecentlyUpdated(payload.productProfileId, 'stock');
                    // If the affected product is in the current cart, re-validate
                    // pricing/availability so the cart auto-clamps to the new stock.
                    const cart = qc.getQueryData(['cart']);
                    const isInCart = cart?.items.some((i) => i.productProfileId === payload.productProfileId);
                    if (isInCart) {
                        void qc.invalidateQueries({ queryKey: ['cart-pricing'] });
                    }
                    break;
                }
                default:
                    break;
            }
        };
        es.onerror = () => {
            // EventSource auto-reconnects on disconnect - just log.
            console.warn('[catalog-events] SSE disconnected, auto-retrying...');
        };
        return () => {
            es.close();
        };
    }, [qc]);
}
const recents = new Map();
const listeners = new Set();
function notify() {
    for (const l of listeners)
        l();
}
function markRecentlyUpdated(id, kind) {
    const at = Date.now();
    recents.set(id, { kind, at });
    notify();
    // Auto-clear after 4 s so the highlight fades out.
    setTimeout(() => {
        const record = recents.get(id);
        if (record?.at === at) {
            recents.delete(id);
            notify();
        }
    }, 4000);
}
/**
 * Returns the kind of the most recent update for a given product id,
 * or null if no update occurred within the last 4 seconds.
 *
 * Subscribes to module-level listeners - re-renders only when the set changes.
 */
export function useRecentUpdate(id) {
    const [, force] = useState({});
    useEffect(() => {
        const l = () => force({});
        listeners.add(l);
        return () => {
            listeners.delete(l);
        };
    }, []);
    return recents.get(id)?.kind ?? null;
}
